import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const inputCls = "bg-black border-white/10 rounded-none text-white focus-visible:ring-lime";

export default function BecomeSponsorDialog() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const empty = { company_name: "", contact_name: "", email: "", phone: "", website: "", country: "", vat_number: "", months_commitment: 6, message: "" };
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sponsor-applications", { ...form, months_commitment: parseInt(form.months_commitment) || 6 });
      toast.success("Request received. Simone will be in touch to arrange the wire.");
      setOpen(false);
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="become-sponsor-btn"
        onClick={() => setOpen(true)}
        className="px-8 py-4 bg-lime text-black font-heading font-black uppercase tracking-wider hover:scale-[1.03] transition-transform"
      >
        Become a sponsor →
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="sponsor-modal" className="max-w-xl bg-surface border-white/10 rounded-none text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black uppercase tracking-tighter">Sponsor PITCH.lu</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">€200/month per company or organization, paid by wire transfer to Kiitos Sarl. Simone will follow up to confirm details and share bank instructions.</p>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Company name *</Label>
                <Input data-testid="sp-company" required value={form.company_name} onChange={set("company_name")} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Contact name *</Label>
                <Input data-testid="sp-contact" required value={form.contact_name} onChange={set("contact_name")} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Email *</Label>
                <Input data-testid="sp-email" required type="email" value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Phone *</Label>
                <Input data-testid="sp-phone" required value={form.phone} onChange={set("phone")} className={`mt-2 ${inputCls}`} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Website *</Label>
                <Input data-testid="sp-website" required value={form.website} onChange={set("website")} className={`mt-2 ${inputCls}`} placeholder="https://…" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Country *</Label>
                <Input data-testid="sp-country" required value={form.country} onChange={set("country")} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">VAT number *</Label>
                <Input data-testid="sp-vat" required value={form.vat_number} onChange={set("vat_number")} className={`mt-2 ${inputCls}`} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Commitment (months) * <span className="text-white/30">min 3</span></Label>
                <Input data-testid="sp-months" required type="number" min="3" max="120" value={form.months_commitment} onChange={set("months_commitment")} className={`mt-2 ${inputCls}`} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Why do you want to support PITCH.lu?</Label>
              <Textarea data-testid="sp-message" rows={3} value={form.message} onChange={set("message")} className={`mt-2 ${inputCls}`} />
            </div>
            <p className="text-white/50 text-xs font-mono uppercase tracking-widest leading-relaxed border-l-2 border-lime pl-3">
              Payment by wire transfer only, due monthly following commercial invoice. Bank details will be shared by a representative of Kiitos S.a.r.l. once your request to become a sponsor is approved.
            </p>
            <button data-testid="sp-submit" type="submit" disabled={busy} className="w-full py-3 bg-lime text-black font-heading font-black uppercase tracking-wider hover:scale-[1.02] transition-transform disabled:opacity-50">
              {busy ? "Submitting…" : "Send request →"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
